import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSetAtom } from 'jotai';
import { get, isEmpty } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { formatTimeseriesData } from '../utils/format';
import instance from '@api/axiosInstance';
import getMetricsData from '@src/api/metricContainer';
import { filterColorSelector, currentMetricsMetaSelector } from '@store/common/selectors';
import { buildMetricSeries } from '../utils/metricSeriesOption';
import { chartOption } from '../utils/chartSchema';
import useTimeseriesQuery from './useTimeseriesQuery';
import { trackQueryError } from '@utils/sensor/apptracker';
import { useSetData, DATA_TAGS, useGetData } from './useData';
import { checkIfSampledCluster } from '@components/common/SampledDataWarning';
import { triggerDeletedFieldsError } from '@utils/errors/triggerPageError';
import { processAnnotations } from '@src/components/common/DataAnnotations';
import { parseUnsupportedMetrics, emptyUnsupportedMetrics } from '../utils/unsupportedMetrics';
import { unsupportedMetricsAtom } from '../atoms';

const defaultMetricValue = { total: null, data: [] };
const defaultResponseValue = { totals: {}, data: {} };

// TODO: test for query optimization
// unique ordered metric key to prevent fetch/render during reorder
// const getMetricsKey = (metrics) => metrics ? JSON.stringify(Array.from(metrics).sort()) : null;

// hook to fetch metric timeseries data
export function useFetchMetricSeries({
  enabled = true,
  useBuildQuery = useTimeseriesQuery,
  dataTag = DATA_TAGS.primary,
}) {
  const {
    query,
    metrics = [],
    interval,
    realTimeOnTrends,
    queryTag,
    currentKpi,
  }: any = useBuildQuery();
  const setMetricData = useSetData({ tag: dataTag, defaultValue: defaultResponseValue });
  const setUnsupportedMetrics = useSetAtom(unsupportedMetricsAtom);

  const {
    isFetchedAfterMount,
    isFetching,
    isError,
    data: response,
    error,
    refetch,
  } = useQuery(
    ['metric-timeseries', query, currentKpi],
    () => {
      return getMetricsData({ queries: [query] }, { headers: { ...queryTag } }, instance).then(
        (response) => {
          const { annotations, data, totals } = get(response, ['data', '0'], {
            totals: {},
            data: [],
            annotations: [],
          });

          // TODO: move annotation parsing to common util - processAnnotations
          checkIfSampledCluster(annotations);
          triggerDeletedFieldsError(annotations);

          processAnnotations(annotations);

          const formattedData = formatTimeseriesData({ metrics, data, interval, annotations });
          return { data: formattedData, totals, unsupported: parseUnsupportedMetrics(annotations) };
        },
      );
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && metrics.length > 0,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: {},
      keepPreviousData: realTimeOnTrends,
      onError: (err) => {
        trackQueryError({ type: 'metric-series', err });
      },
    },
  );

  const formattedData = isEmpty(response) ? defaultResponseValue : response;

  // this state consumed by most components instead of the query hook for perfomance
  useEffect(() => {
    setMetricData(formattedData);
  }, [formattedData, setMetricData]);

  // surface unsupported-metric info (per-card cause message) for the primary query only
  useEffect(() => {
    if (dataTag !== DATA_TAGS.primary) return;
    setUnsupportedMetrics(get(response, 'unsupported', emptyUnsupportedMetrics));
  }, [response, dataTag, setUnsupportedMetrics]);

  return { isFetching, isError, isFetchedAfterMount, refetch, data: formattedData, error };
}

// hook to retrieve data
export const useMetricData = ({ metric = null, dataTag }) => {
  const { data, totals }: any = useGetData({ tag: dataTag, defaultValue: defaultResponseValue });
  if (metric) {
    return data && totals && metric in data
      ? { data: data[metric], total: totals[metric] }
      : defaultMetricValue;
  }
  return { data, totals };
};

// util function to format and inject metric series
export function formatSeries({
  option,
  data = [],
  metric,
  params,
  currentMetricsMeta,
  barChart,
  logAxis,
}) {
  // add or update metric filter series
  const { metricChartData, xAxis, yAxis, tooltip } = buildMetricSeries({
    option,
    data,
    metric,
    params,
    allMetrics: currentMetricsMeta,
    barChart,
    logAxis,
  });
  return { series: [metricChartData], xAxis, yAxis, tooltip };
}

function useMetricSeries({
  metric,
  instance,
  enabled,
  additionalTriggerDeps = [],
  dataTag = DATA_TAGS.primary,
  barChart = false,
  logAxis = false,
}: any) {
  // const unit = useAtomValue(unitAtom);
  const filterColor = useSelector(filterColorSelector);
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const data = useMetricData({ metric, dataTag });

  useEffect(() => {
    if (instance && enabled) {
      let option = instance.getOption();
      if (option) {
        // reset chart when switching from dist to ts
        if (option._viewType !== 'timeseries') {
          const metricChartOption = chartOption({
            metric,
            allMetrics: currentMetricsMeta,
          });
          instance.setOption(metricChartOption, { notMerge: true });
          option = instance.getOption();
        }

        const { data: chartData } = data;
        const { series, xAxis, yAxis, tooltip } = formatSeries({
          option,
          data: chartData,
          metric,
          params: { filterColor },
          currentMetricsMeta,
          barChart,
          logAxis,
        });

        instance.setOption({ series, xAxis, yAxis, tooltip });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, metric, data, filterColor, barChart, logAxis, ...additionalTriggerDeps]);
}

export default useMetricSeries;
